import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Chat, ChatMember } from '@/types/chat';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';

export const useChats = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchChats = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // First, get chat IDs where the user is a member
      const { data: memberChats, error: memberError } = await supabase
        .from('chat_members')
        .select('chat_id')
        .eq('user_id', user.id);

      if (memberError) throw memberError;

      const chatIds = memberChats?.map(m => m.chat_id) || [];
      
      if (chatIds.length === 0) {
        setChats([]);
        setLoading(false);
        return;
      }

      // Now fetch the chats
      const { data, error } = await supabase
        .from('chats')
        .select('*')
        .in('id', chatIds)
        .order('last_message_at', { ascending: false });

      if (error) throw error;

      // Get chat members separately  
      let membersData: any[] = [];
      let messagesData: any[] = [];
      
      const { data: members } = await supabase
        .from('chat_members')
        .select('*')
        .in('chat_id', chatIds);
          
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', members?.map(m => m.user_id) || []);
        
      membersData = members?.map(member => ({
        ...member,
        user: profiles?.find(p => p.id === member.user_id)
      })) || [];

      // Get latest message for each chat
      const { data: messages } = await supabase
        .from('messages')
        .select('*')
        .in('chat_id', chatIds)
        .order('created_at', { ascending: false });
        
      const latestMessages = chatIds.map(chatId => 
        messages?.find(msg => msg.chat_id === chatId)
      ).filter(Boolean);
      
      // Get senders for messages
      const senderIds = latestMessages.map(msg => msg?.sender_id).filter(Boolean);
      const { data: senderProfiles } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', senderIds);
        
      messagesData = latestMessages.map(msg => ({
        ...msg,
        sender: senderProfiles?.find(p => p.id === msg?.sender_id)
      }));

      // Calculate unread counts and format data
      const formattedChats = await Promise.all(
        data?.map(async (chat: any) => {
          // Get user's last_read_at for this chat
          const member = membersData.find((m: any) => m.user_id === user.id && m.chat_id === chat.id);
          const lastReadAt = member?.last_read_at;

          // Count unread messages
          let unreadCount = 0;
          if (lastReadAt) {
            const { count } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .eq('chat_id', chat.id)
              .gt('created_at', lastReadAt)
              .neq('sender_id', user.id); // Don't count own messages

            unreadCount = count || 0;
          }

          return {
            ...chat,
            is_message_request: chat.is_message_request || false,
            members: membersData.filter((m: any) => m.chat_id === chat.id),
            last_message: messagesData.find((m: any) => m.chat_id === chat.id),
            unread_count: unreadCount,
          };
        }) || []
      );

      setChats(formattedChats);
    } catch (error) {
      console.error('Error fetching chats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load chats',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user) return;

    const chatChannel = supabase
      .channel('chats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chats',
        },
        () => {
          fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        () => {
          fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_members',
        },
        () => {
          fetchChats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(chatChannel);
    };
  }, [user, fetchChats]);

  const getTotalUnreadCount = useCallback(() => {
    return chats.reduce((total, chat) => total + (chat.unread_count || 0), 0);
  }, [chats]);

  const getMessageRequestsCount = useMemo(() => {
    return () => chats.filter(chat => {
      const userMembership = chat.members?.find(m => m.user_id === user?.id);
      return chat.is_message_request && userMembership && !userMembership.accepted_at;
    }).length;
  }, [chats, user?.id]);

  const createDMChat = async (otherUserId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_or_create_dm_chat', {
        other_user_id: otherUserId,
      });

      if (error) throw error;
      
      await fetchChats(); // Refresh the chat list
      return data;
    } catch (error) {
      console.error('Error creating DM chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to create chat',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const createGroupChat = async (title: string, memberIds: string[], isBookClub = false) => {
    try {
      const { data, error } = await supabase.rpc('create_group_chat', {
        chat_title: title,
        member_ids: memberIds,
      });

      if (error) throw error;
      
      // Update is_book_club if needed
      if (isBookClub && data) {
        await supabase
          .from('chats')
          .update({ is_book_club: true })
          .eq('id', data);
      }
      
      await fetchChats(); // Refresh the chat list
      return data;
    } catch (error) {
      console.error('Error creating group chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to create group chat',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateChatTitle = async (chatId: string, newTitle: string) => {
    if (!user) throw new Error('Not authenticated');
    
    try {
      const { error } = await supabase
        .from('chats')
        .update({ title: newTitle })
        .eq('id', chatId)
        .eq('created_by', user.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Chat name updated',
      });

      await fetchChats();
    } catch (error) {
      console.error('Error updating chat title:', error);
      toast({
        title: 'Error',
        description: 'Failed to update chat name',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const acceptMessageRequest = async (chatId: string) => {
    if (!user) throw new Error('Not authenticated');
    
    try {
      const { error } = await supabase.rpc('accept_message_request', {
        p_chat_id: chatId,
      });

      if (error) throw error;
      
      sonnerToast.success('Message request accepted');
      await fetchChats();
    } catch (error) {
      console.error('Error accepting message request:', error);
      sonnerToast.error('Failed to accept message request');
      throw error;
    }
  };

  const declineMessageRequest = async (chatId: string) => {
    if (!user) throw new Error('Not authenticated');
    
    try {
      const { error } = await supabase.rpc('decline_message_request', {
        p_chat_id: chatId,
      });

      if (error) throw error;
      
      sonnerToast.success('Message request declined');
      await fetchChats();
    } catch (error) {
      console.error('Error declining message request:', error);
      sonnerToast.error('Failed to decline message request');
      throw error;
    }
  };

  return {
    chats,
    loading,
    getTotalUnreadCount,
    getMessageRequestsCount,
    createDMChat,
    createGroupChat,
    updateChatTitle,
    acceptMessageRequest,
    declineMessageRequest,
    refetch: fetchChats,
  };
};